/**
 * Formats how long an application has been in its current status as a short,
 * relative day count.
 *
 * Uses calendar-day differences (not 24h windows), so an app whose status
 * changed late yesterday reads "yesterday", not "today".
 *
 * - same calendar day  -> "today"
 * - one day earlier     -> "yesterday"
 * - N days earlier      -> "Nd ago"
 * - null / invalid input -> "—"
 *
 * @param dateIso ISO timestamp of the last status change (caller may pass a
 *   `created_at` fallback when `last_status_changed_at` is missing).
 * @param now Reference "now" (injectable for deterministic tests).
 */
export function formatStatusAge(
  dateIso: string | null | undefined,
  now: Date = new Date()
): string {
  if (!dateIso) return "—";

  const date = new Date(dateIso);
  if (Number.isNaN(date.getTime())) return "—";

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  const diffDays = Math.round((startOfToday - startOfDate) / 86_400_000);

  if (diffDays <= 0) return "today";
  if (diffDays === 1) return "yesterday";
  return `${diffDays}d ago`;
}
